import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, Trash2, Loader2, ImageIcon, Info } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useToast } from '../../hooks/use-toast';
import { heroImages } from '../../mock/data';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  { key: 'hero_bg', label: 'Hero Background', desc: 'Full-bleed image behind the homepage headline.', fallback: heroImages.bg, aspect: 'aspect-[16/9]' },
  { key: 'staff_containers', label: 'Team \u2014 Port Operations', desc: 'Shown in the Meet Our Team gallery.', fallback: heroImages.containers, aspect: 'aspect-[3/4]' },
  { key: 'staff_warehouse', label: 'Team \u2014 Warehouse', desc: 'Warehouse operations photo.', fallback: heroImages.team, aspect: 'aspect-[3/4]' },
  { key: 'staff_truck', label: 'Team \u2014 Fleet & Delivery', desc: 'Delivery truck / on-the-road photo.', fallback: heroImages.truck, aspect: 'aspect-[3/4]' },
  { key: 'staff_counter', label: 'Team \u2014 Customer Service', desc: 'Front-counter / customer service photo.', fallback: heroImages.counter, aspect: 'aspect-[3/4]' },
];

const SlotCard = ({ slot, currentValue, onUploaded, onCleared }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const current = currentValue || slot.fallback;
  const isCustom = !!currentValue;

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Please choose an image under 6MB.' });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('slot', slot.key);
      fd.append('file', file);
      await axios.post(`${API}/admin/uploads`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      await onUploaded();
      toast({ title: 'Image updated', description: `${slot.label} is now live on the site.` });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const revert = async () => {
    if (!window.confirm(`Revert ${slot.label} to the default image?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/uploads/${slot.key}`, { headers: { Authorization: `Bearer ${token}` } });
      await onCleared();
      toast({ title: 'Reverted', description: 'Default image restored.' });
    } catch (e) {
      toast({ title: 'Revert failed' });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 overflow-hidden">
      <div className={`relative ${slot.aspect} bg-neutral-100 overflow-hidden`}>
        {current ? (
          <img src={current} alt={slot.label} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-neutral-300"><ImageIcon className="w-10 h-10" /></div>
        )}
        {isCustom && (
          <span className="absolute top-3 left-3 text-[9px] tracking-[0.25em] uppercase bg-[#c8102e] text-white px-2 py-1 font-semibold">Custom</span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-serif-display text-lg font-bold text-black mb-1">{slot.label}</h3>
        <p className="text-xs text-neutral-500 mb-4">{slot.desc}</p>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          <button onClick={() => inputRef.current?.click()} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-black hover:bg-[#c8102e] text-white h-11 px-4 text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {isCustom ? 'Replace' : 'Upload'}
          </button>
          {isCustom && (
            <button onClick={revert} disabled={busy} className="w-11 h-11 flex items-center justify-center border border-neutral-200 hover:border-[#c8102e] hover:bg-[#c8102e] hover:text-white transition-colors disabled:opacity-60" title="Revert to default">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MediaManager = () => {
  const { settings, refresh } = useSiteSettings();
  const val = (slot) => settings?.[`image_${slot}`] || '';

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Media Library</span></div>
        <h1 className="font-serif-display text-4xl font-bold text-black">Photos & Hero Image</h1>
        <p className="text-sm text-neutral-500 mt-2">Upload custom photos for the hero background and the four Meet Our Team spots. Uploads are live instantly.</p>
      </div>

      <div className="bg-neutral-100 border border-neutral-200 p-4 mb-6 flex items-start gap-3">
        <Info className="w-4 h-4 text-neutral-500 mt-0.5 shrink-0" />
        <p className="text-xs text-neutral-600 leading-relaxed">
          Recommended: JPG or PNG, under 6MB. Hero background looks best around 1920x1080 (16:9). Team photos look best portrait, around 900x1200 (3:4). Images are stored securely in your database.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="lg:col-span-2">
          <SlotCard slot={SLOTS[0]} currentValue={val('hero_bg')} onUploaded={refresh} onCleared={refresh} />
        </div>
        {SLOTS.slice(1).map((s) => (
          <SlotCard key={s.key} slot={s} currentValue={val(s.key)} onUploaded={refresh} onCleared={refresh} />
        ))}
      </div>
    </AdminLayout>
  );
};

export default MediaManager;
